/// <amd-module name="VSS/Features/MarkdownEditor/Components/MarkdownTextField" />
import { ObservableLike } from "VSS/Core/Observable";
import * as Resources from "VSS/Features/MarkdownEditor/Resources";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import * as React from "react";
import { ITextRange, applyTextFormatting } from "../Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownFormattingResult, IMarkdownTextSource } from "../Formatting/Contracts";
import { browserSupportsInsertText } from "../Utils/Browser";
import { IMarkdownTextFieldProps } from "./MarkdownTextField.Props";

interface IMarkdownTextFieldState {
    selection?: ITextRange;
}

export class MarkdownTextField extends VssComponent<IMarkdownTextFieldProps, IMarkdownTextFieldState> implements IMarkdownTextSource {
    private textFieldRef = React.createRef<TextField>();

    constructor(props: IMarkdownTextFieldProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {};
    }

    public applyFormatting(formattingResult: IMarkdownFormattingResult): void {
        const textField = this.textFieldRef.current;
        if (textField) {
            // First thing first, get the focus to the input element
            textField.focus();

            if (browserSupportsInsertText()) {
                // This is the preferred way of changing the text since it supports undo/redo
                // but Firefox and IE don't support this
                textField.setSelectionRange(formattingResult.insertSelectionStart, formattingResult.insertSelectionEnd);
                document.execCommand("insertText", false, formattingResult.insertText);
            } else {
                // We will bust undo history unfortunately since insert text is not supported
                this.props.onChange && this.props.onChange(formattingResult.finalText);
            }

            // After we make sure the text is changed, we keep the initial selection to support
            // easy toggle for the commands which support it (bold, italic, etc.)
            this.setState({ selection: { end: formattingResult.finalSelectionEnd, start: formattingResult.finalSelectionStart } });
        }
    }

    public getFormattingArgs(): void | IMarkdownFormattingArgs {
        const textField = this.textFieldRef.current;
        if (textField) {
            const text = ObservableLike.getValue(this.props.value) || "";
            const selectionStart = textField.selectionStart;
            const selectionEnd = textField.selectionEnd;

            return { selectionEnd, selectionStart, text };
        }
    }

    public showError(error: Error | null): void {
        const { onError } = this.props;
        onError && onError(error);
    }

    public render(): JSX.Element {
        const { formItemClassName, error, messageText } = this.props;

        const message = (
            <div className="flex-row">
                <div className="flex flex-center rhythm-horizontal-4 flex-grow">
                    <Icon iconName="Info" />
                    <Link href="http://go.microsoft.com/fwlink/?LinkId=823918" target="_blank" removeUnderline={true}>
                        {Resources.MarkdownSupportedMessage}
                    </Link>
                    {messageText && <span>{messageText}</span>}
                </div>
                {this.props.showInfoLinkForWorkItemLinking && (
                    <div className="flex flex-center rhythm-horizontal-4">
                        <Icon iconName="Info" />
                        <Link href="http://aka.ms/linktoworkitems" target="_blank" removeUnderline={true}>
                            {Resources.LinkWorkItemsMessage}
                        </Link>
                    </div>
                )}
                {this.props.oppositeMessageText && <div className="flex flex-center rhythm-horizontal-4">{this.props.oppositeMessageText}</div>}
            </div>
        );

        return (
            <Observer error={error}>
                {(props: { error: Error | null | undefined }) => {
                    return (
                        <>
                            <FormItem className={formItemClassName} error={!!props.error} message={props.error ? props.error.message : message}>
                                <TextField
                                    autoAdjustHeight={true}
                                    multiline={true}
                                    {...this.props}
                                    onChange={this.onChange}
                                    onKeyDown={this.onKeyDown}
                                    onPaste={this.onPaste}
                                    ref={this.textFieldRef}
                                />
                            </FormItem>
                        </>
                    );
                }}
            </Observer>
        );
    }

    public componentDidUpdate(): void {
        const { selection } = this.state;
        if (this.textFieldRef.current && selection) {
            this.textFieldRef.current.setSelectionRange(selection.start, selection.end);
        }
    }

    private onChange = (event: React.ChangeEvent<HTMLElement>, newValue: string): void => {
        this.props.onChange && this.props.onChange(newValue);
        this.showError(this.getWarningToShow(newValue));
    };

    /**
     * Warning showed as error for now for the sake of simplicity of implementation.
     */
    private getWarningToShow = (newValue: string): Error | null => {
        const { isShowWarning, warningText } = this.props;
        return !!isShowWarning && isShowWarning(newValue) && warningText
            ? {
                  message: warningText,
                  name: warningText
              }
            : null;
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        const { shortcutCommands = [] } = this.props;
        let formattingResult: IMarkdownFormattingResult | Promise<IMarkdownFormattingResult | void> | void;

        if (shortcutCommands.length > 0) {
            const formattingArgs = this.getFormattingArgs();
            for (const shortcut of shortcutCommands) {
                formattingResult = formattingArgs && shortcut.formatText && shortcut.formatText(formattingArgs, event);
                if (formattingResult) {
                    break;
                }
            }
        }

        if (formattingResult) {
            applyTextFormatting(this, formattingResult);
            event.preventDefault();
            event.stopPropagation();
        } else if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }
    };

    private onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
        this.props.onPaste && this.props.onPaste(event, this);
    };
}
